import React from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  useTheme,
  Divider,
} from "@mui/material";
import { useParams } from "react-router-dom";
import moment from "moment-jalaali";

import { useGetAllBlogsQuery } from "../../../Store/Slices/rtk";
import BlogDetailsSkeleton from "./BlogDetailsSkeleton";
import { GradientBorderBox } from "../../../Components";
import SeoHelmet from "../../../Components/SeoHelmet";

export default function BlogDetails() {
  const theme = useTheme();
  const { id } = useParams();
  const { data: dataArr, isLoading } = useGetAllBlogsQuery({
    code: id,
    type: "blogs",
  });

  const data = dataArr?.data?.[0];

  if (isLoading) return <BlogDetailsSkeleton />;

  // SEO configuration
  const seo = data?.SEO;
  const defaultTitle = data?.title
    ? `${data.title} - نیموبایل`
    : "مقاله - نیموبایل";
  const defaultDescription = data?.desc || "مقاله نیموبایل";
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  const articleCoverImage = data?.header_image?.file_name
    ? `${import.meta.env.VITE_URL}${data.header_image.file_name}`
    : undefined;

  const finalOgImage = seo?.og_image?.file_name
    ? `${import.meta.env.VITE_URL}${seo.og_image.file_name}`
    : articleCoverImage;

  return (
    <>
      <SeoHelmet
        title={defaultTitle}
        metaTitle={seo?.meta_title || defaultTitle}
        metaDescription={seo?.metaDescription || defaultDescription}
        metaKeywords={seo?.meta_keywords || "مقاله, وبلاگ, نیموبایل"}
        index={seo?.index !== undefined ? seo.index : true}
        follow={seo?.follow !== undefined ? seo.follow : true}
        ogTitle={seo?.og_title || seo?.meta_title || defaultTitle}
        ogDescription={
          seo?.og_description || seo?.meta_description || defaultDescription
        }
        ogType={seo?.og_type || "article"}
        ogUrl={seo?.og_url || currentUrl}
        ogImage={finalOgImage}
        twitterTitle={
          seo?.twitter?.twitter_title || seo?.meta_title || defaultTitle
        }
        twitterDescription={
          seo?.twitter?.twitter_description ||
          seo?.meta_description ||
          defaultDescription
        }
        twitterCard={seo?.twitter?.twitter_card || "summary_large_image"}
        twitterImage={finalOgImage}
        scheme={seo?.json_schema}
      />

      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          overflow: "hidden",
          py: { xs: 4, md: 8 },
          backgroundColor: theme.palette.background.default,
          direction: "rtl",
        }}
      >
        {/* Ambient Background Lights */}
        <Box
          sx={{
            position: "absolute",
            top: "5%",
            right: "10%",
            width: { xs: "250px", md: "500px" },
            height: { xs: "250px", md: "500px" },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.lowOpacity} 0%, transparent 60%)`,
            filter: "blur(90px)",
            zIndex: 0,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "5%",
            width: { xs: "200px", md: "400px" },
            height: { xs: "200px", md: "400px" },
            borderRadius: "50%",
            background: `radial-gradient(circle, rgba(255,255,255,0.02) 0%, transparent 70%)`,
            filter: "blur(70px)",
            zIndex: 0,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="xxl" sx={{ position: "relative", zIndex: 1 }}>
          <Stack
            sx={{
              width: "100%",
              padding: { xs: "1.5rem", sm: "2.5rem", md: "4rem 5rem" },
              gap: "2.5rem",

              // Premium Glassmorphism Card
              backgroundColor: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              borderRadius: "32px",
              border: "1px solid rgba(255, 255, 255, 0.05)",
              boxShadow:
                "0 10px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
              position: "relative",
            }}
          >
            {/* Header Section */}
            <Stack
              direction={"column"}
              sx={{
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: "1.5rem",
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  lineHeight: 1.4,
                }}
              >
                {data?.title}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "8px 16px",
                  borderRadius: "12px",
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                  border: "1px solid rgba(255,255,255,0.05)",
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.9rem",
                  }}
                >
                  تاریخ انتشار:
                </Typography>
                <Typography
                  sx={{ color: theme.palette.text.primary, fontWeight: "bold" }}
                >
                  {moment.unix(data?.created_at).format("jYYYY/jMM/jDD")}
                </Typography>
              </Box>
            </Stack>

            <Divider
              sx={{
                borderColor: "transparent",
                backgroundImage: `linear-gradient(90deg, transparent, ${theme.palette.primary.lowOpacity}, transparent)`,
                height: "1px",
                opacity: 0.5,
              }}
            />

            {/* Content Section (styled inner HTML for better readability) */}
            <Box
              dangerouslySetInnerHTML={{ __html: data?.body_data || "" }}
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.8,
                fontSize: { xs: "1rem", md: "1.125rem" },

                // Styling nested HTML tags globally for the blog content
                "& p": {
                  marginBottom: "1.5rem",
                  textAlign: "justify",
                },
                "& h2, & h3, & h4": {
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                  marginTop: "2.5rem",
                  marginBottom: "1rem",
                },
                "& h2": {
                  fontSize: "1.5rem",
                  borderBottom: `1px solid rgba(255,255,255,0.05)`,
                  paddingBottom: "0.5rem",
                },
                "& img": {
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "16px",
                  marginTop: "2rem",
                  marginBottom: "2rem",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.4)",
                  border: "1px solid rgba(255,255,255,0.05)",
                },
                "& a": {
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    textDecoration: "underline",
                    color: theme.palette.primary.low,
                  },
                },
                "& ul, & ol": {
                  paddingLeft: "2rem",
                  marginBottom: "1.5rem",
                },
                "& li": {
                  marginBottom: "0.5rem",
                },
                "& strong, & b": {
                  color: theme.palette.text.primary,
                },
                "& blockquote": {
                  borderRight: `4px solid ${theme.palette.primary.main}`,
                  margin: "2rem 0",
                  padding: "1rem 2rem",
                  backgroundColor: "rgba(255,255,255,0.02)",
                  borderRadius: "8px 0 0 8px",
                  fontStyle: "italic",
                },
              }}
            />

            <GradientBorderBox />
          </Stack>
        </Container>
      </Box>
    </>
  );
}
